/**
 * De tien actiekaarten van het basisspel (de gele kaarten).
 *
 * Regelteksten uit `assets/text/basic.txt`, per kaart geciteerd bij de implementatie.
 *
 * Ze worden **uit de hand** gespeeld en kosten geen grondstoffen. Twee dingen gelden voor
 * alle actiekaarten (kennisbank 02 §2.2):
 * - ze mogen pas gespeeld worden als **beide spelers minstens 3 overwinningspunten**
 *   hebben (`msg 63`) — de Verkenner is daarop de uitzondering;
 * - vier ervan gooien met een dobbelsteen, en de tegenstander kan zich met een
 *   *tegenkaart* verweren: de Bisschop tegen Rooftocht en Vuurduivel, de Kruidenheks
 *   tegen de Zwarte ridder. Zo'n tegenkaart maakt de worp ongunstiger voor de aanvaller.
 */
import { BASIC_CARDS, type Resource, type Seat } from '@catan/contracts';
import { roll, type Rng } from './rng.js';
import type { PlayerState } from './state.js';

const byId = new Map(BASIC_CARDS.map((c) => [c.id, c]));
const nameOf = (cardId: number): string => byId.get(cardId)?.name ?? '';

export const ACTION_CARD_NAMES = [
  'Verkenner',
  'Alchimist',
  'Karavaan',
  'Bisschop',
  'Zwarte ridder',
  'Kruidenheks',
  'Vuurduivel',
  'Spion',
  'Rooftocht',
  'Handelaar',
] as const;
export type ActionCardName = (typeof ACTION_CARD_NAMES)[number];

export function actionCardName(cardId: number): ActionCardName | undefined {
  const naam = nameOf(cardId);
  return (ACTION_CARD_NAMES as readonly string[]).includes(naam)
    ? (naam as ActionCardName)
    : undefined;
}

/**
 * Kaarten die je alleen mag spelen zodra beide spelers er drie punten hebben.
 * De Verkenner mag altijd — die hoort bij het bouwen van een dorp.
 */
export function needsThreePoints(naam: ActionCardName): boolean {
  return naam !== 'Verkenner';
}

/** Welke kaart verweert zich tegen welke aanval? */
export const COUNTER_CARDS: Readonly<Record<string, ActionCardName>> = {
  Rooftocht: 'Bisschop',
  Vuurduivel: 'Bisschop',
  'Zwarte ridder': 'Kruidenheks',
};

/**
 * De uitkomst van een aanvalsworp.
 *
 * "Gooi met je dobbelsteen! Bij 1-5 [treft het je tegenstander]. Bij 6 [keert het zich
 * tegen jou]." Speelt de verdediger zijn tegenkaart, dan verliest de tegenstander "ook
 * bij een 3, 4 of 5" — wat betekent dat de aanval alleen nog bij 1 of 2 slaagt.
 */
export interface AttackResult {
  readonly roll: number;
  /** true = de aanvaller slaagt, false = het keert zich tegen hem. */
  readonly success: boolean;
  readonly countered: boolean;
}

export function rollAttack(
  rng: Rng,
  countered: boolean,
): { rng: Rng; result: AttackResult } {
  const worp = roll(rng, 6);
  const success = countered ? worp.value <= 2 : worp.value <= 5;
  return { rng: worp.rng, result: { roll: worp.value, success, countered } };
}

/** Heeft deze speler de tegenkaart tegen `aanval` in zijn hand? */
export function counterInHand(player: PlayerState, aanval: ActionCardName): number | undefined {
  const nodig = COUNTER_CARDS[aanval];
  if (nodig === undefined) return undefined;
  return player.hand.find((id) => nameOf(id) === nodig);
}

/** De gebouwen (geen ridders, geen vloten) die de Vuurduivel kan raken. */
export function destructibleBuildings(
  player: Pick<PlayerState, 'principality'>,
): { column: number; side: 'boven' | 'onder'; cardId: number }[] {
  const uit: { column: number; side: 'boven' | 'onder'; cardId: number }[] = [];
  for (const building of player.principality.buildings) {
    for (const side of ['boven', 'onder'] as const) {
      for (const cardId of building.expansions[side]) {
        const card = byId.get(cardId);
        if (card === undefined) continue;
        const isRidder = card.strength > 0;
        const isVloot = card.name.endsWith('vloot');
        if (!isRidder && !isVloot) uit.push({ column: building.column, side, cardId });
      }
    }
  }
  return uit;
}

/** De ridders die de Zwarte ridder kan raken. */
export function knightsOf(
  player: PlayerState,
): { column: number; side: 'boven' | 'onder'; cardId: number }[] {
  const uit: { column: number; side: 'boven' | 'onder'; cardId: number }[] = [];
  for (const building of player.principality.buildings) {
    for (const side of ['boven', 'onder'] as const) {
      for (const cardId of building.expansions[side]) {
        if ((byId.get(cardId)?.strength ?? 0) > 0) uit.push({ column: building.column, side, cardId });
      }
    }
  }
  return uit;
}

/**
 * **Karavaan** — "Geef 2 van je grondstoffen af en neem daarvoor 2 willekeurige andere
 * grondstoffen terug." De speler kiest wat hij weggeeft; wat hij terugkrijgt is toeval.
 */
export const CARAVAN_TRADE = 2;

/**
 * **Handelaar** — "Neem je tegenstander 2 grondstoffen van je keuze af en geef hem
 * daarvoor een willekeurige grondstof terug."
 */
export const MERCHANT_TAKE = 2;

/**
 * **Rooftocht** — "Bij 1-5 mag je 2 willekeurige grondstoffen van je tegenstander
 * stelen. Bij 6 steelt hij 2 grondstoffen van zijn keuze van jou."
 */
export const RAID_STEAL = 2;

/** Kaartnamen die een dobbelsteenworp vragen. */
export function isAttackCard(naam: ActionCardName): boolean {
  return naam === 'Rooftocht' || naam === 'Vuurduivel' || naam === 'Zwarte ridder';
}

/** Kaarten die alleen als verweer bestaan en niet uit zichzelf gespeeld worden. */
export function isCounterOnly(naam: ActionCardName): boolean {
  return naam === 'Bisschop' || naam === 'Kruidenheks';
}

/** Grondstofsoorten waarvan deze speler er minstens één heeft, met hun landschappen. */
export function resourceSpots(
  player: PlayerState,
  resourceOf: (landscape: string) => Resource,
): { column: number; side: 'boven' | 'onder'; resource: Resource }[] {
  return player.principality.landscapes
    .filter((t) => t.resources > 0)
    .map((t) => ({ column: t.column, side: t.side, resource: resourceOf(t.landscape) }));
}

/** Mag deze speler nu actiekaarten spelen? */
export function actionCardsUnlocked(points: readonly [number, number]): boolean {
  // msg 63: "Je kunt pas actiekaarten spelen als beide spelers over tenminste
  // 3 overwinningspunten beschikken!"
  return points[0] >= 3 && points[1] >= 3;
}

export type { Seat };
